import { useState, useEffect } from "react";
import { useDocHubDialog, EventDialogData } from "@/contexts/DocHubDialogContext";
import { useBrandManager } from "@/hooks/useBrandManager";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/dialog";
import { DialogTitleWithBug } from "@/components/ui/dialog-title-with-bug";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { Upload, Trash2 } from "lucide-react";
import { useDropzone } from "react-dropzone";
import { useTypedTranslation } from "@/lib/i18n/hooks";

export const EventDialog = () => {
  const { currentBrand } = useBrandManager();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTypedTranslation();
  const { dialogType, isOpen, eventData, closeDialog, onSuccess } = useDocHubDialog();
  
  const [formData, setFormData] = useState<EventDialogData>({
    name: '',
    description: '',
    link: '',
  });
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);

  const isEditing = !!eventData?.id;
  const dialogOpen = dialogType === 'event' && isOpen;

  // Sync form data when dialog opens
  useEffect(() => {
    if (eventData && dialogOpen) {
      setFormData({
        id: eventData.id,
        name: eventData.name || '',
        description: eventData.description || '',
        link: eventData.link || '',
      });
    }
  }, [eventData, dialogOpen]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp']
    },
    onDrop: (acceptedFiles) => {
      setUploadedFiles(prev => [...prev, ...acceptedFiles]);
    }
  });

  const createEventMutation = useMutation({
    mutationFn: async () => {
      if (!currentBrand?.id) throw new Error("No brand selected");
      
      const photoUrls: string[] = [];
      
      // Upload photos to storage
      for (const file of uploadedFiles) {
        const fileExt = file.name.split('.').pop();
        const fileName = `${currentBrand.id}/${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
        
        const { error: uploadError } = await supabase.storage
          .from('doc_hub_photos')
          .upload(fileName, file);
        
        if (uploadError) throw uploadError;
        
        const { data: { publicUrl } } = supabase.storage
          .from('doc_hub_photos')
          .getPublicUrl(fileName);
        
        photoUrls.push(publicUrl);
      }
      
      const { data, error } = await supabase
        .from("os_doc_hub_photos")
        .insert({
          brand_id: currentBrand.id,
          user_id: (await supabase.auth.getUser()).data.user?.id,
          name: formData.name,
          description: formData.description || null,
          link: formData.link || null,
          photos: photoUrls,
        })
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["doc-hub-events"] });
      toast({
        title: t('brandSetup.documentationHub.events.eventCreated'),
        description: t('brandSetup.documentationHub.events.eventCreatedDescription'),
      });
      onSuccess?.();
      handleClose(false);
    },
    onError: (error) => {
      toast({
        title: t('brandSetup.documentationHub.events.error'),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateEventMutation = useMutation({
    mutationFn: async () => {
      if (!formData.id) throw new Error("No event selected");
      
      const { error } = await supabase
        .from("os_doc_hub_photos")
        .update({
          name: formData.name,
          description: formData.description || null,
          link: formData.link || null,
        })
        .eq("id", formData.id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["doc-hub-events"] });
      toast({
        title: t('brandSetup.documentationHub.events.eventUpdated'),
        description: t('brandSetup.documentationHub.events.eventUpdatedDescription'),
      });
      onSuccess?.();
      handleClose(false);
    },
    onError: (error) => {
      toast({
        title: t('brandSetup.documentationHub.events.error'),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleRemoveFile = (index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = () => {
    if (!formData.name.trim()) {
      toast({
        title: t('brandSetup.documentationHub.events.error'),
        description: t('brandSetup.documentationHub.events.pleaseEnterEventName'),
        variant: "destructive",
      });
      return;
    }

    if (isEditing) {
      updateEventMutation.mutate();
    } else {
      createEventMutation.mutate();
    }
  };

  const handleClose = (open: boolean) => {
    if (!open) {
      closeDialog();
      setFormData({ name: '', description: '', link: '' });
      setUploadedFiles([]);
    }
  };

  const isPending = createEventMutation.isPending || updateEventMutation.isPending;

  return (
    <Dialog open={dialogOpen} onOpenChange={handleClose} modal={false}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitleWithBug 
            popupName={isEditing ? t('brandSetup.documentationHub.events.editEvent') : t('brandSetup.documentationHub.events.createNewEvent')} 
            popupContext="/documentation-hub/events" 
            isOpen={dialogOpen}
          >
            {isEditing ? t('brandSetup.documentationHub.events.editEvent') : t('brandSetup.documentationHub.events.createNewEvent')}
          </DialogTitleWithBug>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="name">{t('brandSetup.documentationHub.events.name')} *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder={t('brandSetup.documentationHub.events.eventNamePlaceholder')}
            />
          </div>
          <div>
            <Label htmlFor="description">{t('brandSetup.documentationHub.events.description')}</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder={t('brandSetup.documentationHub.events.eventDescriptionPlaceholder')}
            />
          </div>
          <div>
            <Label htmlFor="link">{t('brandSetup.documentationHub.events.link')}</Label>
            <Input
              id="link"
              value={formData.link}
              onChange={(e) => setFormData(prev => ({ ...prev, link: e.target.value }))}
              placeholder={t('brandSetup.documentationHub.events.linkPlaceholder')}
            />
          </div>
          {!isEditing && (
            <div>
              <Label>{t('brandSetup.documentationHub.events.uploadPhotos')}</Label>
              <div
                {...getRootProps()}
                className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                  isDragActive ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/50'
                }`}
              >
                <input {...getInputProps()} />
                <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                {isDragActive ? (
                  <p className="text-sm text-muted-foreground">{t('brandSetup.documentationHub.events.dropPhotosHere')}</p>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    {t('brandSetup.documentationHub.events.dragDropPhotos')}
                  </p>
                )}
              </div>
              {uploadedFiles.length > 0 && (
                <div className="mt-4 space-y-2">
                  <p className="text-sm font-medium">{t('brandSetup.documentationHub.events.selectedPhotos')}</p>
                  {uploadedFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-muted rounded">
                      <span className="text-sm truncate">{file.name}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveFile(index)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => handleClose(false)}>{t('brandSetup.documentationHub.events.cancel')}</Button>
            <Button
              onClick={handleSubmit}
              disabled={!formData.name || isPending}
            >
              {isPending 
                ? (isEditing ? t('brandSetup.documentationHub.events.updating') : t('brandSetup.documentationHub.events.creating'))
                : (isEditing ? t('brandSetup.documentationHub.events.update') : t('brandSetup.documentationHub.events.createEvent'))}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
